/* eslint-disable no-unused-vars */
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Card, Collapse, OverlayTrigger, Tooltip } from "react-bootstrap";
import GooglePlacesAutocomplete from "react-google-places-autocomplete";
import { Helmet } from "react-helmet";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import ReactQuill from "react-quill";
import { useNavigate, Link } from "react-router-dom";
import { MultiSelect, } from "react-multi-select-component";

import Breadcrumbs from "../../../../components/customBreadcrumb/customBreadcrumb";
import CustomButton from "../../../../components/customButton/customButton";
import CustomeShimer from "../../../../components/customeShimer/customeShimer";
import FileUpload from "../../../../components/fileUpload/fileUpload";
import ImageCropModal from "../../../../components/imageCropModal/imageCropModal";
import HookForm from "../../../../components/hookForm/hookForm";
import useWindowDimension from "../../../../components/hooks/useWindowDimension";
import TextField1 from "../../../../components/textField/textField1";
import TicketTypeSelect from "../../../../components/ticketTypeSelect/TicketTypeSelect";
import Calender from "../../../calender/calender";
import CustomeSelect from "../../../customSelect/customSelect";
import CustomToggleButton from "../../../customToggleButton/customToggleButton";
import EventFormPreview from "./EventFormPreview";
import EventTableBookingSection from "./EventTableBookingSection";
import EventCatalogAttachSection from "../../../clubCatalog/EventCatalogAttachSection";
// import TicketPhaseBuilder from "../../../eventPricing/TicketPhaseBuilder";
// import { applyPhaseTotalsToTicket } from "../../../../helpers/ticketSalePhases";
import "../../../../pages/eventPricingHub/eventPricingHub.css";
import "./addTicketTable.css";

import { getCloudinaryMediaBaseUrl } from "../../../../config/cloudinaryBase";
import { getAutoPassesPerUnit } from "../../../../config/predefinedTicketTypes";
import { resolveCloudinaryImageSrc } from "../../../../helpers/helper.service";
import Constant from "../../../../util/constant";

const EVENT_IMAGE_ASPECT = 4 / 5;
const EVENT_IMAGE_WIDTH = 1200;
const EVENT_IMAGE_HEIGHT = 1500;
const LAYOUT_IMAGE_ASPECT = 4 / 3;

function isRhfFieldError(val) {
  return (
    val &&
    typeof val === "object" &&
    typeof val.type === "string" &&
    !Array.isArray(val)
  );
}

const CREATE_EVENT_FIELD_ORDER = [
  "title",
  "eventstartDate",
  "eventEndDate",
  "bookingdate",
  "bookingEnd",
  "language",
  "genreid",
  "club",
  "artist",
  "category",
  "layoutdesc",
  "map",
  "about",
  "terms",
  "bookingpercentage",
  "tableBookingFixedFee",
  "tickets",
];

const TICKET_FIELD_ORDER = [
  "tickettype",
  "gstPercent",
  "ticketprice",
  "totaltickets",
  "passesPerUnit",
  "coverAmount",
  "deliverycost",
  "categorydesc",
];

function replaceDoubleSpaceWithSemicolon(value) {
  return String(value ?? "").replace(/  +/g, ";");
}

function collectInvalidFieldPaths(errors, prefix = "") {
  const paths = [];
  if (!errors || typeof errors !== "object") return paths;
  for (const key of Object.keys(errors)) {
    const val = errors[key];
    if (!val) continue;
    const path = prefix ? `${prefix}.${key}` : key;
    if (isRhfFieldError(val)) {
      paths.push(path);
      continue;
    }
    if (Array.isArray(val)) {
      for (let i = 0; i < val.length; i++) {
        const item = val[i];
        if (!item) continue;
        paths.push(...collectInvalidFieldPaths(item, `${path}.${i}`));
      }
    } else if (typeof val === "object") {
      paths.push(...collectInvalidFieldPaths(val, path));
    }
  }
  return paths;
}

function getFieldSortIndex(path) {
  const ticketMatch = path.match(/^tickets\.(\d+)\.(.+)$/);
  if (ticketMatch) {
    const ticketIndex = Number(ticketMatch[1]);
    const fieldName = ticketMatch[2];
    const ticketsOrder = CREATE_EVENT_FIELD_ORDER.indexOf("tickets");
    const fieldOrder = TICKET_FIELD_ORDER.indexOf(fieldName);
    return (
      (ticketsOrder === -1 ? 999 : ticketsOrder) * 100000 +
      ticketIndex * 1000 +
      (fieldOrder === -1 ? 999 : fieldOrder)
    );
  }
  const topLevel = path.split(".")[0];
  const order = CREATE_EVENT_FIELD_ORDER.indexOf(topLevel);
  return (order === -1 ? 999 : order) * 100000;
}

/** First invalid field path in visual form order (e.g. tickets.0.tickettype). */
function getFirstInvalidFieldPath(errors) {
  const paths = collectInvalidFieldPaths(errors);
  if (!paths.length) return null;
  return [...paths].sort(
    (left, right) => getFieldSortIndex(left) - getFieldSortIndex(right)
  )[0];
}

function getErrorByPath(errors, path) {
  if (!errors || !path) return undefined;
  return path.split(".").reduce((current, segment) => {
    if (current == null) return undefined;
    return current[segment];
  }, errors);
}

function hasErrorAtPath(errors, path) {
  return getErrorByPath(errors, path) != null;
}

function markAllInvalidFieldsInDom(paths) {
  const root = document.querySelector(".mainCreateEditEvent");
  if (!root) return;

  root.querySelectorAll(".form-control.field-show-error").forEach((el) => {
    el.classList.remove("field-show-error", "invalid-field-focused");
  });

  paths.forEach((path) => {
    const byName = root.querySelector(`[name="${CSS.escape(path)}"]`);
    if (byName) {
      byName.classList.add("is-invalid", "field-show-error");
      return;
    }

    const byData = root.querySelector(`[data-rhf-field="${CSS.escape(path)}"]`);
    if (byData) {
      byData.classList.add("rhf-field-error", "field-show-error");
    }
  });
}

function focusFieldByPath(path) {
  if (!path) return;

  const scrollTarget = (element) => {
    element?.scrollIntoView?.({ behavior: "smooth", block: "center" });
  };

  const clearInvalidFocusMarks = () => {
    document
      .querySelectorAll(".mainCreateEditEvent .invalid-field-focused")
      .forEach((el) => el.classList.remove("invalid-field-focused"));
  };

  const markInvalidFocused = (element) => {
    if (!element?.classList) return;
    clearInvalidFocusMarks();
    if (
      element.matches?.(
        'input.form-control, textarea.form-control, input:not([type="hidden"]), textarea'
      )
    ) {
      element.classList.add("is-invalid", "invalid-field-focused");
    }
  };

  const tryFocus = (element) => {
    if (!element) return false;
    if (typeof element.focus === "function") {
      element.focus({ preventScroll: true });
      return true;
    }
    return false;
  };

  const byName = document.querySelector(`[name="${CSS.escape(path)}"]`);
  if (byName) {
    markInvalidFocused(byName);
    tryFocus(byName);
    scrollTarget(byName);
    return;
  }

  const byDataField = document.querySelector(
    `[data-rhf-field="${CSS.escape(path)}"]`
  );
  if (byDataField) {
    const focusable =
      byDataField.querySelector(
        'input:not([type="hidden"]), textarea, button.customDateTrigger, .dropdown-heading, .ql-editor, [role="combobox"]'
      ) || byDataField;
    tryFocus(focusable);
    scrollTarget(byDataField);
    return;
  }

  if (path === "about" || path.startsWith("about.")) {
    const wrap = document.getElementById("create-event-about-editor");
    const editor = wrap?.querySelector?.(".ql-editor");
    tryFocus(editor);
    scrollTarget(wrap);
    return;
  }

  if (path === "terms" || path.startsWith("terms.")) {
    const wrap = document.getElementById("create-event-terms-editor");
    const editor = wrap?.querySelector?.(".ql-editor");
    tryFocus(editor);
    scrollTarget(wrap);
    return;
  }

  const invalid = document.querySelector(".mainCreateEditEvent .is-invalid");
  if (invalid) {
    tryFocus(invalid);
    scrollTarget(invalid);
  }
}

const CreateEditEventComponent1 = (props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dimensions = useWindowDimension();
  const [aboutSectionOpen, setAboutSectionOpen] = useState(true);
  const [termsSectionOpen, setTermsSectionOpen] = useState(true);

  const {
    fields,
    append,
    prepend,
    remove,
    swap,
    move,
    insert,
    replace,
    register,
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    errors,
    trigger,
    setFocus,
    formState,
    //above is all new hook form
    placeHolderShimer,
    eventForm,
    selectInputRef,
    genresList,
    onChangeArtistType,
    onChangeLanguage,
    onChangeOrganiserType,
    onChangeCategory,
    formatOptionLabel,
    components,
    handleTextTitleChange,
    languages,
    uploadThumNailValid,
    handleUploadThumnail,
    handleUploadThumnailFile,
    createEventFunction,
    handleFormSubmit,
    handleOpenEventPreview,
    handleCloseEventPreview,
    eventPreview,
    artistSelectInputRef,
    languageSelectInputRef,
    organiserSelectInputRef,
    clubSelectInputRef,
    showClubField,
    showTableBookingField,
    effectiveClubIdForTableBooking,
    effectiveClubIdForCatalog,
    catalogEventItem,
    catalogManageHref,
    canManageCatalog,
    allowOrganiserPick,
    showOfflineEmployeeField,
    clubEmployeeOptions,
    clubEmployeesLoading,
    clubOptionsForEventForm,
    isCreatePage,
    saveCreateEventDraftNow,
    categorySelectInputRef,
    genresSelectInputRef,
    isUploadingDocumentLink,
    profileImage,
    setIsUploadingDocumentLink,
    createEventLoaderButton,
    uploadedDocumentThumnailFile,
    handleClearUploadedThumnailFile,
    triggerRef,
    breadcrumbHeight,
    dimenstionError,
    setDimenstionError,
    expireformattedDate,
    organiser,
    aboutTextEditorValue,
    setAboutTextEditorValue,
    termsTextEditorValue,
    setTermsTextEditorValue,
    reactQuillRefIns,
    reactQuillaboutRefIns,
    setAboutTextEditorValueCount,
    aboutTextEditorValueCount,
    setTermsTextEditorValueCount,
    termsTextEditorValueCount,
    artists,
    categories,
    pageTitle,
    bookingformattedDate,

    onChangeAutocomplate,
    locationValue,
    state,
    onBackArrow,
    languagedefaultValue,
    onChangeGenre,
    genresdefaultValue,
    organiserdefaultValue,
    artistsdefaultValue,
    categorydefaultValue,
    expireformattedDateEnd,
    bookingexpireformattedDateEnd,

    daynamicImage,

    formats,
    handleKeyDown,
    // CustomLabel,
    eventStartingDate,
    onChangeEventStartingDate,
    onChangeBookingStartingDate,
    bookingStartingDate,
    onChangeBookingEndingDate,
    bookingEndingDate,
    onChangeEventEndingDate,
    eventEndingDate,
    isEventEndInvalid,
    isEventStartInvalid,
    isBookingStartInvalid,
    isBookingEndInvalid,
    ticketRows,
    CustomLabelEventStart,
    CustomLabelEventEnd,
    CustomLabelBookingStart,
    CustomLabelBookingEnd,
    // layout image
    handleUploadLayoutThumnail,
    handleUploadLayoutThumnailFile,
    setIsUploadingLayoutImageLink,
    isUploadingLayoutImageLink,
    uploadedLayoutImageFile,
    handleClearUploadedLayoutImageFile,
    uploadLayoutThumNailValid,
    islayoutImage,
    daynamicLayoutImage,
    selectedCategories,
    setCategories,
    selectedArtist,
    setSelectedArtist,
    selectedLanguages,
    setSelectedLanguages,
    selectedGenre,
    setSelectedGenre,
  } = props;

  const fieldHasError = useCallback(
    (path) => hasErrorAtPath(errors, path),
    [errors]
  );

  const handleCategoryDescKeyDown = useCallback(
    (e) => {
      if (e.key === " ") {
        const input = e.target;
        const pos = input.selectionStart;
        if (
          pos === input.selectionEnd &&
          pos > 0 &&
          input.value.charAt(pos - 1) === " "
        ) {
          e.preventDefault();
          const fieldName = input.name;
          const newValue =
            input.value.slice(0, pos - 1) + ";" + input.value.slice(pos);
          setValue(fieldName, newValue, { shouldDirty: true });
          requestAnimationFrame(() => {
            input.setSelectionRange(pos, pos);
          });
          return;
        }
      }
      handleKeyDown(e);
    },
    [handleKeyDown, setValue]
  );

  const handleCategoryDescChange = useCallback(
    (fieldName) => (e) => {
      const next = replaceDoubleSpaceWithSemicolon(e.target.value);
      if (next !== e.target.value) {
        setValue(fieldName, next, { shouldDirty: true });
      }
    },
    [setValue]
  );

  const handleInvalidSubmit = useCallback(
    (submitErrors) => {
      const paths = collectInvalidFieldPaths(submitErrors);
      if (!paths.length) return;

      const path = getFirstInvalidFieldPath(submitErrors);
      markAllInvalidFieldsInDom(paths);

      if (path === "about" || path.startsWith("about.")) {
        setAboutSectionOpen(true);
      }
      if (path === "terms" || path.startsWith("terms.")) {
        setTermsSectionOpen(true);
      }

      const focusAndScroll = () => {
        markAllInvalidFieldsInDom(paths);
        if (typeof setFocus === "function") {
          try {
            setFocus(path, { shouldSelect: true });
          } catch {
            /* path may not be registered for focus */
          }
        }
        focusFieldByPath(path);
      };

      requestAnimationFrame(() => {
        if (path.startsWith("about") || path.startsWith("terms")) {
          setTimeout(focusAndScroll, 320);
        } else {
          focusAndScroll();
        }
      });
    },
    [setFocus]
  );

  const CLOUDINARY_URL = getCloudinaryMediaBaseUrl();
  const defaultValue = useMemo(
    () => ({
      label: locationValue,
      value: locationValue,
    }),
    [locationValue]
  );
  const checkCharacterCount = (event) => {
    const unprivilegedEditor = reactQuillRefIns.current.unprivilegedEditor;
    if (unprivilegedEditor.getLength() > 2000 && event.key !== "Backspace")
      event.preventDefault();
  };
  const checkCharacterCountAbout = (event) => {
    const unprivilegedEditor = reactQuillaboutRefIns.current.unprivilegedEditor;
    if (unprivilegedEditor.getLength() > 2000 && event.key !== "Backspace")
      event.preventDefault();
  };

  const thumbnailFieldOnChangeRef = useRef(() => {});
  const layoutFieldOnChangeRef = useRef(() => {});
  const [cropModalSession, setCropModalSession] = useState(null);
  const cropModalSessionRef = useRef(null);

  useEffect(() => {
    cropModalSessionRef.current = cropModalSession;
  }, [cropModalSession]);

  useEffect(() => {
    const u = cropModalSession?.src;
    return () => {
      if (u) URL.revokeObjectURL(u);
    };
  }, [cropModalSession?.src]);

  const beginImageCrop = (file, field) => {
    const isThumbnail = field === "thumbnail";
    const baseName =
      file.name.replace(/\.[^/.]+$/, "") ||
      (isThumbnail ? "event-poster" : "layout");
    setCropModalSession({
      src: URL.createObjectURL(file),
      mime: file.type,
      baseName,
      field,
      aspect: isThumbnail ? EVENT_IMAGE_ASPECT : LAYOUT_IMAGE_ASPECT,
      outputWidth: isThumbnail ? EVENT_IMAGE_WIDTH : undefined,
      outputHeight: isThumbnail ? EVENT_IMAGE_HEIGHT : undefined,
      hintKey: isThumbnail ? "IMAGE_CROP_HINT_EVENT" : "IMAGE_CROP_HINT",
    });
  };

  const handleCroppedImageApplied = (file) => {
    const session = cropModalSessionRef.current;
    if (!session) return;
    const { field } = session;
    const list = [file];
    if (field === "thumbnail") {
      handleUploadThumnail(
        list,
        setIsUploadingDocumentLink,
        handleUploadThumnailFile
      );
      thumbnailFieldOnChangeRef.current?.(list);
    } else if (field === "layout") {
      handleUploadLayoutThumnail(
        list,
        setIsUploadingLayoutImageLink,
        handleUploadLayoutThumnailFile
      );
      layoutFieldOnChangeRef.current?.(list);
    }
    setDimenstionError(false);
    setCropModalSession(null);
  };

  const handleAddTicket = () => {
    const nextIndex = fields.length;
    append({
      _id: "",
      tickettype: "",
      categorydesc: "",
      ticketprice: "",
      gstPercent: "0",
      totaltickets: "",
      deliverycost: "",
      passesPerUnit: "1",
      coverAmount: "0",
      soldout: false,
      offlineSoldOut: false,
      phasedPricingEnabled: false,
      phases: [],
    });
    requestAnimationFrame(() => {
      setTimeout(() => {
        setFocus?.(`tickets.${nextIndex}.tickettype`);
      }, 0);
    });
  };

  return (
    <>
      <React.Fragment>
        <Helmet>
          <title>
            {state?.page === "add"
              ? "Create Event | RizzTix"
              : "Edit Event | RizzTix"}
          </title>
        </Helmet>
        <div className="pageContent createEditEvent">
          <Breadcrumbs
            innerPagetitle={
              state?.page === "add" ? t("ADD_EVENT") : t("EDIT_EVENT")
            }
            breadcrumbItem={
              state?.page === "add" ? t("ADD_EVENT") : t("EDIT_EVENT")
            }
            pageTitle={pageTitle}
            BreadcrumbInner={true}
            BreadcrumbmainTitle={true}
            backArrow={true}
            onBackArrow={onBackArrow}
          />

          <Card
            className="mainCard mainCreateEditEvent"
            style={{
              height: dimensions.height - breadcrumbHeight - 100,
            }}
          >
            <Card.Body className="activeTabSupplier">
              <div className="mainCardBodyPart">
                <form
                  noValidate
                  onSubmit={handleSubmit(
                    handleOpenEventPreview,
                    handleInvalidSubmit
                  )}
                >
                  <div className="event-form-editor">
                    <div className="p-0 mainRowStyle">
                      <div className="leftPart">
                        <div className="leftContentPart">
                          <div className="filuploadArea">
                            <div className="wrapper-input uploadLink">
                              {placeHolderShimer && <CustomeShimer />}

                              <div className="textFiledWrapper wrapper-input imgUpload">
                                <div className="imageFile">
                                  <div className="imagePlaceholderWrapper">
                                    <div className="imagePlaceHolder">
                                      <span
                                        className="clearimage"
                                        onClick={() => {
                                          handleClearUploadedThumnailFile();
                                          setValue("thumbnailfile", "", {
                                            shouldDirty: true,
                                          });
                                        }}
                                      >
                                        <div className="imgBoxEmpty deleteBtn">
                                          <i className="bx bxs-trash "></i>
                                          <p className="upload_text">
                                            Delete Picture
                                          </p>
                                        </div>
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                <Controller
                                  errors={errors}
                                  name={eventForm.thumbnailfile.name}
                                  rules={eventForm.thumbnailfile.validate}
                                  control={control}
                                  render={({ field: { onChange, value } }) => {
                                    thumbnailFieldOnChangeRef.current =
                                      onChange;
                                    return (
                                    <>
                                      <FileUpload
                                        onDrop={async (acceptedFiles) => {
                                          const validFileTypes =
                                            Object.keys(uploadThumNailValid);
                                          const validFiles =
                                            acceptedFiles.filter((file) =>
                                              validFileTypes.includes(file.type)
                                            );

                                          if (validFiles?.length > 0) {
                                            beginImageCrop(
                                              validFiles[0],
                                              "thumbnail"
                                            );
                                          } else {
                                            handleUploadThumnail(
                                              validFiles,
                                              setIsUploadingDocumentLink,
                                              handleUploadThumnailFile
                                            );

                                            setDimenstionError(true);
                                            onChange(acceptedFiles);
                                          }
                                        }}
                                        maxSize={10 * 1024 * 1024}
                                        accept={uploadThumNailValid}
                                        triggerOpen={triggerRef}
                                      >
                                        <>
                                          {isUploadingDocumentLink && (
                                            <>
                                              <div className="progressWrapper">
                                                <div className="progress">
                                                  <div className="progressValue"></div>
                                                </div>
                                                <p>{t("UPLOADINGONLY")}</p>
                                              </div>
                                            </>
                                          )}
                                          {!isUploadingDocumentLink &&
                                            profileImage && (
                                              <img
                                                src={profileImage}
                                                alt="placeholder"
                                              ></img>
                                            )}
                                          {!isUploadingDocumentLink &&
                                            !profileImage && (
                                              <div className="dropZoneWrapper">
                                                {daynamicImage ===
                                                  undefined ||
                                                daynamicImage === "" ? (
                                                  <div className="placeholder-style">
                                                    {t("UPLOAD_IMAGE")}
                                                  </div>
                                                ) : (
                                                  <img
                                                    src={resolveCloudinaryImageSrc(
                                                      daynamicImage,
                                                      CLOUDINARY_URL
                                                    )}
                                                    alt="eventImage"
                                                  />
                                                )}
                                              </div>
                                            )}
                                        </>
                                        {dimenstionError && (
                                          <p className="errorMsg ">
                                            {t("THE_IMAGE_RATIO_SHOULD_BE_4_5")}
                                          </p>
                                        )}
                                        <div className="imgBoxEmpty uploadBtn">
                                          <i className="bx bxs-image"></i>
                                          <p className="upload_text">
                                            Change Picture
                                          </p>
                                        </div>
                                      </FileUpload>
                                    </>
                                  );
                                  }}
                                />
                              </div>
                            </div>
                          </div>

                          <div className="textFeildArea">
                            <div className="d-flex">
                              <p className="form-label">
                                {placeHolderShimer && <CustomeShimer />}
                                {t("TITLE_LABLE")}
                              </p>
                              <p className="requiredStyle">
                                * {placeHolderShimer && <CustomeShimer />}
                              </p>
                            </div>
                            <div
                              className={`wrapper-input${
                                fieldHasError("title") ? " rhf-field-error" : ""
                              }`}
                              data-rhf-field="title"
                            >
                              {placeHolderShimer && <CustomeShimer />}
                              <TextField1
                                control={control}
                                watchValue={watch(eventForm.title.name)}
                                rules={eventForm.title.validate}
                                name={eventForm.title.name}
                                errors={errors}
                                forceShowError={fieldHasError("title")}
                                autoFocus={false}
                                required={false}
                                type="text"
                                placeholder={t("ENTER_EVENT_TITLE")}
                                onChange={handleTextTitleChange}
                                maxLength="70"
                              />
                            </div>

                            <div className="twoFeilds">
                              <div className="wrapper-input">
                                <div className="d-flex">
                                  <p className="form-label">
                                    {placeHolderShimer && <CustomeShimer />}
                                    {t("EVENT_START_EVENT_TIMINGS")}
                                  </p>
                                  <p className="requiredStyle">*</p>
                                </div>
                                {placeHolderShimer && <CustomeShimer />}
                                <div
                                  className={`dateWrapper${
                                    fieldHasError("eventstartDate")
                                      ? " rhf-field-error"
                                      : ""
                                  }`}
                                >
                                  <Controller
                                    name={eventForm.eventstartDate.name}
                                    rules={eventForm.eventstartDate.validate}
                                    control={control}
                                    render={({
                                      field: { onChange, value },
                                    }) => (
                                      <>
                                        <Calender
                                          selected={eventStartingDate}
                                          withPortal={false}
                                          popperPlacement="bottom-start"
                                          popperProps={{ strategy: "fixed" }}
                                          showTimeSelect={true}
                                          timeFormat="p"
                                          dateFormat="Pp"
                                          minDate={new Date()}
                                          minTime={
                                            moment(eventStartingDate).format(
                                              "ll"
                                            ) ===
                                              moment(new Date()).format("ll")
                                              ? new Date()
                                              : new Date().setHours(0, 0)
                                          }
                                          maxTime={new Date().setHours(23, 59)}
                                          timeIntervals={30}
                                          onChange={(date) => {
                                            onChange(date);
                                            onChangeEventStartingDate(date);
                                          }}
                                          customInput={
                                            <CustomLabelEventStart />
                                          }
                                          value={eventStartingDate}
                                          isClearable={
                                            eventStartingDate ? true : false
                                          }
                                        />
                                        {isEventStartInvalid && (
                                          <span className="errorMsg customError">
                                            <span>{t("INVALID_DATE")}</span>
                                          </span>
                                        )}
                                        {fieldHasError("eventstartDate") &&
                                          errors.eventstartDate && (
                                            <span className="errorMsg customError">
                                              <span>
                                                {errors.eventstartDate.message}
                                              </span>
                                            </span>
                                          )}
                                      </>
                                    )}
                                  />
                                </div>
                              </div>
                              <div className="wrapper-input">
                                <div className="d-flex">
                                  <p className="form-label">
                                    {t("EVENT_END_EVENT_TIMINGS")}
                                  </p>
                                  <p className="requiredStyle">*</p>
                                </div>
                                {placeHolderShimer && <CustomeShimer />}
                                <div
                                  className={`dateWrapper${
                                    fieldHasError("eventEndDate")
                                      ? " rhf-field-error"
                                      : ""
                                  }`}
                                >
                                  <Controller
                                    name={eventForm.eventEndDate.name}
                                    rules={eventForm.eventEndDate.validate}
                                    control={control}
                                    render={({
                                      field: { onChange, value },
                                    }) => (
                                      <>
                                        <Calender
                                          selected={eventEndingDate}
                                          withPortal={false}
                                          popperPlacement="bottom-start"
                                          popperProps={{ strategy: "fixed" }}
                                          showTimeSelect={true}
                                          timeFormat="p"
                                          dateFormat="Pp"
                                          minDate={new Date(eventStartingDate)}
                                          minTime={
                                            moment(eventEndingDate).format(
                                              "ll"
                                            ) ===
                                              moment(
                                                new Date(eventStartingDate)
                                              ).format("ll")
                                              ? new Date(
                                                new Date(
                                                  eventStartingDate
                                                ).setMinutes(
                                                  new Date(
                                                    eventStartingDate
                                                  ).getMinutes() + 30
                                                )
                                              )
                                              : new Date().setHours(0, 0)
                                          }
                                          maxTime={new Date().setHours(23, 59)}
                                          timeIntervals={30}
                                          onChange={(date) => {
                                            onChange(date);
                                            onChangeEventEndingDate(date);
                                          }}
                                          customInput={<CustomLabelEventEnd />}
                                          value={eventEndingDate}
                                          isClearable={
                                            eventEndingDate ? true : false
                                          }
                                        />
                                        {isEventEndInvalid && (
                                          <span className="errorMsg customError">
                                            <span>{t("INVALID_DATE")}</span>
                                          </span>
                                        )}
                                        {fieldHasError("eventEndDate") &&
                                          errors.eventEndDate && (
                                            <span className="errorMsg customError">
                                              <span>
                                                {errors.eventEndDate.message}
                                              </span>
                                            </span>
                                          )}
                                      </>
                                    )}
                                  />
                                </div>
                              </div>
                            </div>
                            <div className="twoFeilds">
                              <div className="wrapper-input btmLeftfeild">
                                <div className="d-flex">
                                  <p className="form-label">
                                    {t("BOOKING_START_TIME")}
                                  </p>
                                  <p className="requiredStyle">*</p>
                                </div>
                                {placeHolderShimer && <CustomeShimer />}
                                <div
                                  className={`dateWrapper${
                                    fieldHasError("bookingdate")
                                      ? " rhf-field-error"
                                      : ""
                                  }`}
                                >
                                  <Controller
                                    name={eventForm.bookingdate.name}
                                    rules={eventForm.bookingdate.validate}
                                    control={control}
                                    render={({
                                      field: { onChange, value },
                                    }) => (
                                      <>
                                        <Calender
                                          selected={bookingStartingDate}
                                          withPortal={false}
                                          popperPlacement="bottom-start"
                                          popperProps={{ strategy: "fixed" }}
                                          showTimeSelect={true}
                                          timeFormat="p"
                                          dateFormat="Pp"
                                          minDate={new Date()}
                                          maxDate={new Date(eventStartingDate)}
                                          minTime={
                                            moment(bookingStartingDate).format(
                                              "ll"
                                            ) ===
                                              moment(new Date()).format("ll")
                                              ? new Date()
                                              : new Date().setHours(0, 0)
                                          }
                                          maxTime={
                                            moment(bookingStartingDate).format(
                                              "ll"
                                            ) ===
                                              moment(eventStartingDate).format(
                                                "ll"
                                              )
                                              ? new Date(eventStartingDate)
                                              : new Date().setHours(23, 59)
                                          }
                                          timeIntervals={30}
                                          onChange={(date) => {
                                            onChange(date);
                                            onChangeBookingStartingDate(date);
                                          }}
                                          customInput={
                                            <CustomLabelBookingStart />
                                          }
                                          value={bookingStartingDate}
                                          isClearable={
                                            bookingStartingDate ? true : false
                                          }
                                        />
                                        {isBookingStartInvalid && (
                                          <span className="errorMsg customError">
                                            <span>{t("INVALID_DATE")}</span>
                                          </span>
                                        )}
                                        {fieldHasError("bookingdate") &&
                                          errors.bookingdate && (
                                            <span className="errorMsg customError">
                                              <span>
                                                {errors.bookingdate.message}
                                              </span>
                                            </span>
                                          )}
                                      </>
                                    )}
                                  />
                                </div>
                              </div>
                              <div className="wrapper-input btmLeftfeild">
                                <div className="d-flex">
                                  <p className="form-label">
                                    {t("Booking End Time")}
                                  </p>
                                  <p className="requiredStyle">*</p>
                                </div>
                                {placeHolderShimer && <CustomeShimer />}
                                <div
                                  className={`dateWrapper${
                                    fieldHasError("bookingEnd")
                                      ? " rhf-field-error"
                                      : ""
                                  }`}
                                >
                                  <Controller
                                    name={eventForm.bookingEnd.name}
                                    rules={eventForm.bookingEnd.validate}
                                    control={control}
                                    render={({
                                      field: { onChange, value },
                                    }) => (
                                      <>
                                        <Calender
                                          selected={bookingEndingDate}
                                          withPortal={false}
                                          popperPlacement="bottom-start"
                                          popperProps={{ strategy: "fixed" }}
                                          showTimeSelect={true}
                                          timeFormat="p"
                                          dateFormat="Pp"
                                          minDate={
                                            new Date(bookingStartingDate)
                                          }
                                          maxDate={
                                            moment(eventEndingDate).isValid()
                                              ? new Date(eventEndingDate)
                                              : new Date(eventStartingDate)
                                          }
                                          minTime={
                                            moment(bookingStartingDate).format(
                                              "ll"
                                            ) ===
                                              moment(bookingEndingDate).format(
                                                "ll"
                                              )
                                              ? new Date(
                                                new Date(
                                                  bookingStartingDate
                                                ).setMinutes(
                                                  new Date(
                                                    bookingStartingDate
                                                  ).getMinutes() + 30
                                                )
                                              )
                                              : new Date().setHours(0, 0)
                                          }
                                          maxTime={
                                            moment(eventEndingDate).isValid() &&
                                            moment(eventEndingDate).format(
                                              "ll"
                                            ) ===
                                              moment(bookingEndingDate).format(
                                                "ll"
                                              )
                                              ? new Date(eventEndingDate)
                                              : new Date().setHours(23, 59)
                                          }
                                          timeIntervals={30}
                                          onChange={(date) => {
                                            onChange(date);
                                            onChangeBookingEndingDate(date);
                                          }}
                                          customInput={
                                            <CustomLabelBookingEnd />
                                          }
                                          value={bookingEndingDate}
                                          isClearable={
                                            bookingEndingDate ? true : false
                                          }
                                        />
                                        {isBookingEndInvalid && (
                                          <span className="errorMsg customError">
                                            <span>{t("INVALID_DATE")}</span>
                                          </span>
                                        )}
                                        {fieldHasError("bookingEnd") &&
                                          errors.bookingEnd && (
                                            <span className="errorMsg customError">
                                              <span>
                                                {errors.bookingEnd.message}
                                              </span>
                                            </span>
                                          )}
                                      </>
                                    )}
                                  />
                                </div>
                              </div>
                            </div>
                            <div className="twoFeilds singleSelectDropdown imageNone">
                              <div className="wrapper-input">
                                {placeHolderShimer && <CustomeShimer />}
                                <div className="d-flex">
                                  <p className="form-label">{t("LANGUAGE")}</p>
                                  <p className="requiredStyle">*</p>
                                </div>

                                <Controller
                                  name={eventForm.language.name}
                                  rules={eventForm.language.validate}
                                  control={control}
                                  render={({ field: { onChange, value } }) => (
                                    <>
                                      {/* <CustomeSelect
                                        ref={languageSelectInputRef}
                                        options={languages}
                                        closeMenuOnSelect={false}
                                        isSearchable={true}
                                        isClearable={true}
                                        placeholder={t("SELECT")}
                                        onChange={(selectedOptions) => {
                                          const limitedSelection =
                                            selectedOptions.slice(0, 5);
                                          onChangeLanguage(limitedSelection);

                                          onChange(limitedSelection);
                                        }}
                                        formatOptionLabel={formatOptionLabel}
                                        components={components}
                                        isDisabled={false}
                                        value={languagedefaultValue}
                                        isMulti={true}
                                        className="multiSelecter customMultiDropdown"
                                      /> */}
                                      <div
                                        data-rhf-field="language"
                                        className={
                                          fieldHasError("language")
                                            ? "rhf-field-error"
                                            : ""
                                        }
                                      >
                                        <MultiSelect
                                          className={"multiSelectDropdownStyle"}
                                          options={languages}
                                          value={selectedLanguages}
                                          hasSelectAll={false}
                                          onChange={(selected) => {
                                            setSelectedLanguages(selected);
                                            onChange(
                                              selected.map((option) => option.id)
                                            );
                                          }}
                                          labelledBy="Select"
                                        />
                                      </div>
                                      {/* {selectedLanguages?.length > 1 ? (
                                        <p className="multiselectTag">
                                          +{selectedLanguages?.length - 1}
                                        </p>
                                      ) : (
                                        ""
                                      )} */}
                                      {fieldHasError("language") &&
                                        errors.language && (
                                          <div className="errorMsg">
                                            <span>
                                              {errors.language.message ||
                                                errors.language.minSelected
                                                  ?.message ||
                                                t("REQUIRED")}
                                            </span>
                                          </div>
                                        )}
                                    </>
                                  )}
                                />
                              </div>
                              <div className="wrapper-input">
                                {placeHolderShimer && <CustomeShimer />}
                                <div className="d-flex">
                                  <p className="form-label">{t("GENERES")}</p>
                                  <p className="requiredStyle">*</p>
                                </div>

                                <Controller
                                  name={eventForm.genreid.name}
                                  rules={eventForm.genreid.validate}
                                  control={control}
                                  render={({ field: { onChange, value } }) => (
                                    <>
                                      {/* <CustomeSelect
                                        ref={genresSelectInputRef}
                                        options={genresList}
                                        closeMenuOnSelect={false}
                                        isSearchable={true}
                                        isClearable={true}
                                        placeholder={t("SELECT")}
                                        onChange={(selectedOptions) => {
                                          const limitedSelection =
                                            selectedOptions.slice(0, 5);
                                          onChangeGenre(limitedSelection);

                                          onChange(limitedSelection);
                                        }}
                                        formatOptionLabel={formatOptionLabel}
                                        components={components}
                                        isDisabled={false}
                                        value={genresdefaultValue}
                                        isMulti={true}
                                        className="multiSelecter customMultiDropdown"
                                      /> */}
                                      <div
                                        data-rhf-field="genreid"
                                        className={
                                          fieldHasError("genreid")
                                            ? "rhf-field-error"
                                            : ""
                                        }
                                      >
                                        <MultiSelect
                                          className={"multiSelectDropdownStyle"}
                                          options={genresList}
                                          value={selectedGenre}
                                          hasSelectAll={false}
                                          onChange={(selected) => {
                                            setSelectedGenre(selected);
                                            onChange(
                                              selected.map((option) => option.id)
                                            );
                                          }}
                                          labelledBy="Select"
                                        />
                                      </div>
                                      {/* {selectedGenre?.length > 1 ? (
                                        <p className="multiselectTag">
                                          +{selectedGenre?.length - 1}
                                        </p>
                                      ) : (
                                        ""
                                      )} */}
                                      {fieldHasError("genreid") &&
                                        errors.genreid && (
                                          <div className="errorMsg">
                                            <span>
                                              {errors.genreid.message ||
                                                errors.genreid.minSelected
                                                  ?.message ||
                                                t("REQUIRED")}
                                            </span>
                                          </div>
                                        )}
                                    </>
                                  )}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="leftBottomPart">
                          <div className="d-flex">
                            <p className="form-label">
                              {placeHolderShimer && <CustomeShimer />}
                              {t("ASSIGN_ORGANISER")}
                            </p>
                            <p className="requiredStyle">
                              * {placeHolderShimer && <CustomeShimer />}
                            </p>
                          </div>
                          <div className={"wrapper-input btmDrpdown "}>
                            {placeHolderShimer && <CustomeShimer />}
                        <Controller
                              name={eventForm.organiser.name}
                              rules={eventForm.organiser.validate}
                              control={control}
                              render={({ field: { onChange, value } }) => (
                                <>
                                  <CustomeSelect
                                    ref={organiserSelectInputRef}
                                    options={organiser}
                                    closeMenuOnSelect={false}
                                    isSearchable={false}
                                    isClearable={true}
                                    placeholder={t("SELECT")}
                                    onChange={(item) => {
                                      onChangeOrganiserType(item);
                                      onChange(item);
                                    }}
                                    formatOptionLabel={formatOptionLabel}
                                    components={components}
                                    value={organiserdefaultValue}
                                    isDisabled={!allowOrganiserPick}
                                    isMulti={false}
                                    className={
                                      "singleSelecter organiserDropdownStyle"
                                    }
                                  />
                                </>
                              )}
                            />
                          </div>
                          {showClubField && (
                            <>
                              <div className="d-flex mt-3">
                                <p className="form-label">
                                  {placeHolderShimer && <CustomeShimer />}
                                  {t("EVENT_CLUB_OPTIONAL")}
                                </p>
                              </div>
                              <div className={"wrapper-input btmDrpdown "}>
                                {placeHolderShimer && <CustomeShimer />}
                                <Controller
                                  name={eventForm.club.name}
                                  rules={eventForm.club.validate}
                                  control={control}
                                  render={({ field: { onChange, value } }) => (
                                    <CustomeSelect
                                      ref={clubSelectInputRef}
                                      options={clubOptionsForEventForm}
                                      closeMenuOnSelect={true}
                                      isSearchable={true}
                                      isClearable={true}
                                      placeholder={t("SELECT")}
                                      onChange={(item) => onChange(item)}
                                      formatOptionLabel={formatOptionLabel}
                                      components={components}
                                      value={value}
                                      isDisabled={false}
                                      isMulti={false}
                                      className={
                                        "singleSelecter organiserDropdownStyle"
                                      }
                                    />
                                  )}
                                />
                              </div>
                            </>
                          )}
                          {showOfflineEmployeeField ? (
                            <>
                              <div className="d-flex mt-3">
                                <p className="form-label mb-0">
                                  {placeHolderShimer && <CustomeShimer />}
                                  {t("ASSIGN_EMPLOYEES_AND_OPERATORS")}
                                </p>
                              </div>
                              <div
                                className="wrapper-input btmDrpdown"
                                style={{
                                  opacity: clubEmployeesLoading ? 0.65 : 1,
                                  pointerEvents: clubEmployeesLoading
                                    ? "none"
                                    : "auto",
                                }}
                              >
                                {placeHolderShimer && <CustomeShimer />}
                                <Controller
                                  name="offlineAssignedUserIds"
                                  control={control}
                                  render={({ field: { onChange, value } }) => (
                                    <MultiSelect
                                      className={"multiSelectDropdownStyle"}
                                      options={clubEmployeeOptions || []}
                                      value={(clubEmployeeOptions || []).filter(
                                        (o) =>
                                          Array.isArray(value) &&
                                          value.some(
                                            (v) =>
                                              String(v) === String(o?.id ?? "")
                                          )
                                      )}
                                      hasSelectAll={true}
                                      onChange={(selected) => {
                                        onChange(
                                          (selected || [])
                                            .map((s) => s.id)
                                            .filter(Boolean)
                                        );
                                      }}
                                      labelledBy={t("SELECT")}
                                      overrideStrings={{
                                        selectAll: t("SELECT_ALL"),
                                        allItemsAreSelected: t("ALL_SELECTED"),
                                      }}
                                    />
                                  )}
                                />
                              </div>
                            </>
                          ) : null}
                          <div className="d-flex">
                            <p className="form-label">
                              {placeHolderShimer && <CustomeShimer />}
                              {t("ARTISTS_LABEL")}
                            </p>
                            <p className="requiredStyle">
                              * {placeHolderShimer && <CustomeShimer />}
                            </p>
                          </div>
                          <div className="wrapper-input btmDrpdown">
                            {placeHolderShimer && <CustomeShimer />}
                            <Controller
                              name={eventForm.artist.name}
                              rules={eventForm.artist.validate}
                              control={control}
                              render={({ field: { onChange, value } }) => (
                                <>
                                  {/* <CustomeSelect
                                    ref={artistSelectInputRef}
                                    options={artists}
                                    closeMenuOnSelect={false}
                                    isSearchable={true}
                                    isClearable={true}
                                    placeholder={t("SELECT")}
                                    onChange={(selectedOptions) => {
                                      const limitedSelection =
                                        selectedOptions.slice(0, 5);
                                      onChangeArtistType(limitedSelection);

                                      onChange(limitedSelection);
                                    }}
                                    formatOptionLabel={formatOptionLabel}
                                    components={components}
                                    isDisabled={false}
                                    value={artistsdefaultValue}
                                    isMulti={true}
                                    className="multiSelecter customMultiDropdown"
                                  /> */}

                                  <div
                                    data-rhf-field="artist"
                                    className={
                                      fieldHasError("artist")
                                        ? "rhf-field-error"
                                        : ""
                                    }
                                  >
                                    <MultiSelect
                                      className={"multiSelectDropdownStyle"}
                                      options={artists}
                                      value={selectedArtist}
                                      hasSelectAll={false}
                                      onChange={(selected) => {
                                        setSelectedArtist(selected);
                                        onChange(
                                          selected.map((option) => option.id)
                                        );
                                      }}
                                      labelledBy="Select"
                                    />
                                  </div>
                                  {/* {selectedArtist?.length > 1 ? (
                                    <p className="multiselectTag custom-selecttag-style">
                                      +{selectedArtist?.length - 1}
                                    </p>
                                  ) : (
                                    ""
                                  )} */}
                                  {fieldHasError("artist") && errors.artist && (
                                    <div className="errorMsg">
                                      <span>
                                        {errors.artist.message ||
                                          errors.artist.minSelected?.message ||
                                          t("REQUIRED")}
                                      </span>
                                    </div>
                                  )}
                                </>
                              )}
                            />
                          </div>
                          <div className="d-flex">
                            <p className="form-label">
                              {placeHolderShimer && <CustomeShimer />}
                              {t("CATEGORIES")}
                            </p>
                            <p className="requiredStyle">
                              * {placeHolderShimer && <CustomeShimer />}
                            </p>
                          </div>
                          <div className="wrapper-input btmDrpdown">
                            {placeHolderShimer && <CustomeShimer />}
                            <Controller
                              name={eventForm.category.name}
                              rules={eventForm.category.validate}
                              control={control}
                              render={({ field: { onChange, value } }) => (
                                <>
                                  {/* <CustomeSelect
                                    ref={categorySelectInputRef}
                                    options={categories}
                                    closeMenuOnSelect={false}
                                    isClearable={true}
                                    placeholder={t("SELECT")}
                                    isSearchable={true}
                                    onChange={(selectedOptions) => {
                                      const limitedSelection =
                                        selectedOptions.slice(0, 5);
                                      onChangeCategory(limitedSelection);

                                      onChange(limitedSelection);
                                    }}
                                    value={categorydefaultValue}
                                    components={components}
                                    isDisabled={false}
                                    isMulti={true}
                                    className="multiSelecter"
                                  /> */}
                                  <div
                                    data-rhf-field="category"
                                    className={
                                      fieldHasError("category")
                                        ? "rhf-field-error"
                                        : ""
                                    }
                                  >
                                    <MultiSelect
                                      className={"multiSelectDropdownStyle"}
                                      options={categories}
                                      value={selectedCategories}
                                      hasSelectAll={false}
                                      onChange={(selected) => {
                                        setCategories(selected);
                                        onChange(
                                          selected.map((option) => option.id)
                                        );
                                      }}
                                      labelledBy="Select"
                                    />
                                  </div>
                                  {/* {selectedCategories?.length > 1 ? (
                                    <p className="multiselectTag custom-selecttag-style">
                                      +{selectedCategories?.length - 1}
                                    </p>
                                  ) : (
                                    ""
                                  )} */}
                                  {fieldHasError("category") &&
                                    errors.category && (
                                      <div className="errorMsg">
                                        <span>
                                          {errors.category.message ||
                                            errors.category.minSelected
                                              ?.message ||
                                            t("REQUIRED")}
                                        </span>
                                      </div>
                                    )}
                                </>
                              )}
                            />
                            {/* {console.log("selectedCategories----", selectedCategories)} */}
                          </div>
                          <div className="layoutImageDescriptionStyle">
                            <div className="filuploadArea layoutImageStyle">
                              <div className="uploadLink">
                                {placeHolderShimer && <CustomeShimer />}
                                <div className="d-flex">
                                  <p className="form-label">
                                    {placeHolderShimer && <CustomeShimer />}
                                    {t("LAYOUT_IMAGE")}
                                  </p>
                                </div>
                                <div className="textFiledWrapper wrapper-input imgUpload">
                                  <div className="imageFile">
                                    <div className="imagePlaceholderWrapper">
                                      <div className="imagePlaceHolder">
                                        <span
                                          className="clearimage"
                                          onClick={() => {
                                            handleClearUploadedLayoutImageFile();
                                            setValue("layoutimage", "", {
                                              shouldDirty: true,
                                            });
                                          }}
                                        >
                                          <div className="imgBoxEmpty deleteBtn">
                                            <i className="bx bxs-trash "></i>
                                          </div>
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                  <Controller
                                    errors={errors}
                                    name={eventForm?.layoutimage?.name}
                                    rules={eventForm?.layoutimage?.validate}
                                    control={control}
                                    render={({
                                      field: { onChange, value },
                                    }) => {
                                      layoutFieldOnChangeRef.current =
                                        onChange;
                                      return (
                                      <>
                                        <FileUpload
                                          onDrop={async (acceptedFiles) => {
                                            const validFileTypes = Object.keys(
                                              uploadLayoutThumNailValid
                                            );
                                            const validFiles =
                                              acceptedFiles.filter((file) =>
                                                validFileTypes.includes(
                                                  file.type
                                                )
                                              );

                                            if (validFiles?.length > 0) {
                                              beginImageCrop(
                                                validFiles[0],
                                                "layout"
                                              );
                                            } else {
                                              handleUploadLayoutThumnail(
                                                validFiles,
                                                setIsUploadingLayoutImageLink,
                                                handleUploadLayoutThumnailFile
                                              );

                                              setDimenstionError(true);
                                              onChange(acceptedFiles);
                                            }
                                          }}
                                          maxSize={10 * 1024 * 1024} // 10 MB
                                          accept={uploadLayoutThumNailValid}
                                          triggerOpen={triggerRef}
                                        >
                                          <>
                                            {isUploadingLayoutImageLink && (
                                              <>
                                                <div className="progressWrapper">
                                                  <div className="progress">
                                                    <div className="progressValue"></div>
                                                  </div>
                                                  <p>{t("UPLOADINGONLY")}</p>
                                                </div>
                                              </>
                                            )}
                                            {!isUploadingLayoutImageLink &&
                                              islayoutImage && (
                                                <img
                                                  src={islayoutImage}
                                                  alt="placeholder"
                                                ></img>
                                              )}
                                            {!isUploadingLayoutImageLink &&
                                              !islayoutImage && (
                                                <div className="dropZoneWrapper layoutImage">
                                                  {daynamicLayoutImage ===
                                                    undefined ||
                                                  daynamicLayoutImage ===
                                                    "" ? (
                                                    <div className="placeholder-style">
                                                      {t("UPLOAD_IMAGE")}
                                                    </div>
                                                  ) : (
                                                    <img
                                                      src={resolveCloudinaryImageSrc(
                                                        daynamicLayoutImage,
                                                        CLOUDINARY_URL
                                                      )}
                                                      alt="profie"
                                                    />
                                                  )}
                                                </div>
                                              )}
                                          </>
                                          {dimenstionError && (
                                            <p className="errorMsg ">
                                              {t(
                                                "THE_IMAGE_RATIO_SHOULD_BE_4_3"
                                              )}
                                            </p>
                                          )}
                                        </FileUpload>
                                      </>
                                      );
                                      }}
                                  />
                                </div>
                              </div>
                            </div>
                            <div className="layoutDescriptionStyle">
                              <div className="d-flex">
                                <p className="form-label">
                                  {placeHolderShimer && <CustomeShimer />}
                                  {t("LAYOUT_DESCRIPTION")}
                                </p>
                              </div>
                              <div className=" wrapper-input">
                                {placeHolderShimer && <CustomeShimer />}
                                <TextField1
                                  control={control}
                                  watchValue={watch(eventForm.layoutdesc.name)}
                                  rules={eventForm?.layoutdesc?.validate}
                                  name={eventForm?.layoutdesc?.name}
                                  errors={errors}
                                  autoFocus={false}
                                  required={false}
                                  placeholder={t("LAYOUT_DESCRIPTION")}
                                  onChange={handleTextTitleChange}
                                  maxLength="2000"
                                  textarea="textarea"
                                  rows={6}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="rightPart">
                        <div className="rightTopPart">
                          <div className="locationFeild wrapper-input btmLeftfeild">
                            <div className="d-flex">
                              <p className="form-label">
                                {placeHolderShimer && <CustomeShimer />}
                                {t("LOCATION")}
                              </p>
                              <p className="requiredStyle">
                                * {placeHolderShimer && <CustomeShimer />}
                              </p>
                            </div>
                            <div
                              className={`google-dropdown-style${
                                fieldHasError("map") ? " rhf-field-error" : ""
                              }`}
                              data-rhf-field="map"
                            >
                              {placeHolderShimer && <CustomeShimer />}
                              <Controller
                                name={eventForm.map.name}
                                control={control}
                                rules={eventForm.map.validate}
                                render={({ field: { onChange, value } }) => (
                                  <GooglePlacesAutocomplete
                                    autocompletionRequest={{
                                      componentRestrictions: {
                                        country: ["in"],
                                      },
                                    }}
                                    apiKey={Constant.MAP_KEY}
                                    // defaultValue={defaultValue}
                                    selectProps={{
                                      value:
                                        state?.page === "add"
                                          ? defaultValue?.label === "" ||
                                            defaultValue?.label === undefined
                                            ? ""
                                            : defaultValue
                                          : defaultValue?.label === undefined
                                            ? ""
                                            : defaultValue,
                                      onChange: async (e) => {
                                        onChange(e?.label);
                                        onChangeAutocomplate(e);
                                      },
                                      backspaceRemovesValue: true,
                                      controlShouldRenderValue: true,
                                      isClearable: true,
                                      placeholder: t("ENTER_EVENT_lOCATION"),
                                      styles: {
                                        input: (provided) => ({
                                          ...provided,
                                          color: "white",
                                          height: "36",
                                        }),
                                        option: (provided) => ({
                                          ...provided,
                                          color: "black",
                                        }),
                                        singleValue: (provided) => ({
                                          ...provided,
                                          color: "white",
                                          height: "36",
                                        }),
                                      },
                                    }}
                                  />
                                )}
                              />
                              {fieldHasError("map") && errors.map && (
                                <div className="errorMsg">
                                  <span>{errors.map.message}</span>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="d-flex locationToggleStyle">
                            <p className="form-label">
                              {t("VENUE_TO_BE_ANNOUNCE")}
                            </p>
                            <div className="toggleRightStyle">
                              <Controller
                                name={eventForm.venuetobeanounced.name}
                                rules={eventForm.venuetobeanounced.validate}
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                  <>
                                    <CustomToggleButton
                                      name={eventForm.venuetobeanounced.name}
                                      rules={
                                        eventForm.venuetobeanounced.validate
                                      }
                                      control={control}
                                      checked={value}
                                      onCheckChange={onChange}
                                    />
                                  </>
                                )}
                              />
                            </div>
                          </div>

                          <div className="wrapper-input btmDrpdown aboutStyle">
                            <button
                              type="button"
                              className="createEditEventSectionToggle"
                              onClick={() =>
                                setAboutSectionOpen((open) => !open)
                              }
                              aria-expanded={aboutSectionOpen}
                              aria-controls="create-event-about-editor"
                            >
                              <i
                                className={`bx fs-5 flex-shrink-0 createEditEventSectionChevron ${
                                  aboutSectionOpen
                                    ? "bx-chevron-up"
                                    : "bx-chevron-down"
                                }`}
                                aria-hidden="true"
                              />
                              <div className="d-flex">
                                <p className="form-label">{t("ABOUT")}</p>
                                <p className="requiredStyle">*</p>
                              </div>
                            </button>
                            {placeHolderShimer && <CustomeShimer />}
                            <Collapse in={aboutSectionOpen}>
                              <div
                                id="create-event-about-editor"
                                data-rhf-field="about"
                                className={
                                  fieldHasError("about") ? "rhf-field-error" : ""
                                }
                              >
                                <div className="customQuillEditoAbout">
                                  <Controller
                                    name={eventForm.about.name}
                                    control={control}
                                    rules={eventForm.about.validate}
                                    render={({
                                      field: { onChange, value },
                                    }) => (
                                      <ReactQuill
                                        placeholder={t(
                                          "DESCRIPTION_PLACEHOLDER"
                                        )}
                                        value={aboutTextEditorValue}
                                        ref={reactQuillaboutRefIns}
                                        onKeyDown={checkCharacterCountAbout}
                                        modules={{
                                          toolbar: [
                                            ["bold", "italic", "underline"],
                                            [{ list: "ordered" }],
                                          ],
                                        }}
                                        formats={formats}
                                        onChange={(
                                          content,
                                          delta,
                                          source,
                                          editor,
                                          e
                                        ) => {
                                          const textContentone =
                                            editor.getText(content);
                                          setAboutTextEditorValue(content);
                                          onChange(content);
                                          setAboutTextEditorValueCount(
                                            textContentone?.length
                                          );
                                          const quill =
                                            reactQuillaboutRefIns?.current?.getEditor();
                                          quill?.on(
                                            "text-change",
                                            function (
                                              delta,
                                              old,
                                              source
                                            ) {
                                              if (quill?.getLength() > 1999) {
                                                quill?.deleteText(
                                                  2000,
                                                  quill?.getLength()
                                                );
                                              }
                                            }
                                          );
                                        }}
                                      />
                                    )}
                                  />

                                  <div className="customQuillEditorCount">
                                    {aboutTextEditorValueCount <= 1 ? (
                                      <span className="errorMsg">
                                        {" "}
                                        <span>
                                          {" "}
                                          {t("THIS_FIELD_IS_REQUIRED")}
                                        </span>
                                      </span>
                                    ) : (
                                      <span className="errorMsg"></span>
                                    )}
                                    <span className="badgecount badge">
                                      {aboutTextEditorValueCount > 0
                                        ? aboutTextEditorValueCount - 1
                                        : 0}
                                      /2000
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </Collapse>
                          </div>
                        </div>
                        <div className="wrapper-input terms">
                          <button
                            type="button"
                            className="createEditEventSectionToggle"
                            onClick={() =>
                              setTermsSectionOpen((open) => !open)
                            }
                            aria-expanded={termsSectionOpen}
                            aria-controls="create-event-terms-editor"
                          >
                            <i
                              className={`bx fs-5 flex-shrink-0 createEditEventSectionChevron ${
                                termsSectionOpen
                                  ? "bx-chevron-up"
                                  : "bx-chevron-down"
                              }`}
                              aria-hidden="true"
                            />
                            <div className="d-flex">
                              <p className="form-label">{t("TERMS")}</p>
                              <p className="requiredStyle">*</p>
                            </div>
                          </button>
                          {placeHolderShimer && <CustomeShimer />}
                          <Collapse in={termsSectionOpen}>
                            <div
                              id="create-event-terms-editor"
                              data-rhf-field="terms"
                              className={
                                fieldHasError("terms") ? "rhf-field-error" : ""
                              }
                            >
                              <div className="customQuillEditoAbout">
                                <Controller
                                  name={eventForm.terms.name}
                                  control={control}
                                  rules={eventForm.terms.validate}
                                  render={({ field: { onChange, value } }) => (
                                    <ReactQuill
                                      placeholder={t(
                                        "DESCRIPTION_PLACEHOLDER"
                                      )}
                                      value={termsTextEditorValue}
                                      ref={reactQuillRefIns}
                                      onKeyDown={checkCharacterCount}
                                      modules={{
                                        toolbar: [
                                          ["bold", "italic", "underline"],
                                          [{ list: "ordered" }],
                                        ],
                                      }}
                                      formats={formats}
                                      onChange={(
                                        content,
                                        delta,
                                        source,
                                        editor,
                                        e
                                      ) => {
                                        const quill =
                                          reactQuillRefIns?.current?.getEditor();
                                        quill?.on(
                                          "text-change",
                                          function (
                                            delta,
                                            old,
                                            source
                                          ) {
                                            if (quill?.getLength() > 1999) {
                                              quill?.deleteText(
                                                2000,
                                                quill?.getLength()
                                              );
                                            }
                                          }
                                        );

                                        const textContentone =
                                          editor.getText(content);
                                        setTermsTextEditorValue(content);
                                        onChange(content);
                                        setTermsTextEditorValueCount(
                                          textContentone?.length
                                        );
                                      }}
                                    />
                                  )}
                                />
                                <div className="customQuillEditorCount">
                                  {termsTextEditorValueCount <= 1 ? (
                                    <span className="errorMsg">
                                      {" "}
                                      <span>
                                        {" "}
                                        {t("THIS_FIELD_IS_REQUIRED")}
                                      </span>
                                    </span>
                                  ) : (
                                    <span className="errorMsg"></span>
                                  )}
                                  <span className="badgecount badge">
                                    {termsTextEditorValueCount > 0
                                      ? termsTextEditorValueCount - 1
                                      : 0}
                                    /2000
                                  </span>
                                </div>
                              </div>
                            </div>
                          </Collapse>
                        </div>
                        <div className="d-flex locationToggleStyle sponsoredEventsStyle">
                          <p className="form-label">{t("SPONSORED_EVENTS")}</p>
                          <div className="toggleRightStyle">
                            <Controller
                              name={eventForm.sponsored.name}
                              rules={eventForm.sponsored.validate}
                              control={control}
                              render={({ field: { onChange, value } }) => (
                                <>
                                  <CustomToggleButton
                                    name={eventForm.sponsored.name}
                                    rules={eventForm.sponsored.validate}
                                    control={control}
                                    checked={value}
                                    onCheckChange={onChange}
                                  />
                                </>
                              )}
                            />
                          </div>
                        </div>
                        <div className="d-flex locationToggleStyle sponsoredEventsStyle">
                          <p className="form-label">18+ Event</p>
                          <div className="toggleRightStyle">
                            <Controller
                              name={eventForm.is18Plus.name}
                              rules={eventForm.is18Plus.validate}
                              control={control}
                              render={({ field: { onChange, value } }) => (
                                <CustomToggleButton
                                  name={eventForm.is18Plus.name}
                                  rules={eventForm.is18Plus.validate}
                                  control={control}
                                  checked={Boolean(value)}
                                  onCheckChange={onChange}
                                />
                              )}
                            />
                          </div>
                        </div>
                        <div className="event-booking-fee-pair">
                          <div className="d-flex">
                            <p className="form-label">
                              {placeHolderShimer && <CustomeShimer />}
                              {t("BOOKING_%")}
                            </p>
                            <p className="requiredStyle">
                              * {placeHolderShimer && <CustomeShimer />}
                            </p>
                          </div>
                          <div
                            className={`wrapper-input bookingParent${
                              fieldHasError("bookingpercentage")
                                ? " rhf-field-error"
                                : ""
                            }`}
                            data-rhf-field="bookingpercentage"
                          >
                            {placeHolderShimer && <CustomeShimer />}
                            <TextField1
                              control={control}
                              watchValue={watch(eventForm.bookingpercentage.name)}
                              rules={eventForm?.bookingpercentage?.validate}
                              name={eventForm?.bookingpercentage?.name}
                              errors={errors}
                              forceShowError={fieldHasError("bookingpercentage")}
                              autoFocus={false}
                              required={true}
                              type="text"
                              placeholder={t("ENTER_BOOKING_%")}
                              onChange={handleTextTitleChange}
                              maxLength={5}
                            />
                          </div>
                          {showTableBookingField ? (
                            <>
                              <div className="d-flex">
                                <p className="form-label">
                                  {placeHolderShimer && <CustomeShimer />}
                                  {t("TABLE_BOOKING_FIXED_FEE")}
                                </p>
                                <p className="requiredStyle">
                                  * {placeHolderShimer && <CustomeShimer />}
                                </p>
                              </div>
                              <div
                                className={`wrapper-input bookingParent${
                                  fieldHasError("tableBookingFixedFee")
                                    ? " rhf-field-error"
                                    : ""
                                }`}
                                data-rhf-field="tableBookingFixedFee"
                              >
                                {placeHolderShimer && <CustomeShimer />}
                                <TextField1
                                  control={control}
                                  watchValue={watch(eventForm.tableBookingFixedFee.name)}
                                  rules={eventForm?.tableBookingFixedFee?.validate}
                                  name={eventForm?.tableBookingFixedFee?.name}
                                  errors={errors}
                                  forceShowError={fieldHasError("tableBookingFixedFee")}
                                  autoFocus={false}
                                  required={true}
                                  type="text"
                                  placeholder="200"
                                  maxLength={10}
                                />
                              </div>
                            </>
                          ) : null}
                        </div>
                      </div>
                    </div>
                    <div className="titleBtnRowStlye">
                      <div className="addTicketTitleStyle">
                        {t("ADD_TICKET")}
                      </div>
                      <button
                        className="addTicketBtnStyle"
                        onClick={handleAddTicket}
                        type="button"
                      >
                        <i className="mdi mdi-plus-box-outline"></i>
                        {t("ADD_TICKET")}
                      </button>
                    </div>
                    <div className="addTicketCardStyle">
                      <div className="addTicketTableScroll">
                      <div className="addTicketRowHeadingStyle">
                        <div className="input-column-style ticket-type-column">
                          <div className="d-flex">
                            <p className="form-label">
                              {placeHolderShimer && <CustomeShimer />}
                              {t("TICKET_TYPE")}
                            </p>
                            <p className="requiredStyle">
                              * {placeHolderShimer && <CustomeShimer />}
                            </p>
                          </div>
                        </div>
                        <div className="input-column-style ticket-gst-column">
                          <div className="d-flex">
                            <p className="form-label">{t("TICKET_GST_PERCENT")}</p>
                          </div>
                        </div>
                        <div className="input-column-style ticket-price-column">
                          <div className="d-flex">
                            <p className="form-label">{t("TICKET_PRICE")}</p>
                            <p className="requiredStyle">*</p>
                          </div>
                        </div>

                        <div className="input-column-style ticket-total-column">
                          <div className="d-flex">
                            <p className="form-label">
                              {t("TOTAL_NUMBER_OF_TICKETS")}
                              <p style={{ display: 'inline' }} className="requiredStyle">*</p>
                            </p>
                          </div>
                        </div>
                        <div className="input-column-style ticket-passes-column">
                          <div className="d-flex">
                            <p className="form-label">
                              {t("TICKET_PASSES_PER_UNIT")}
                            </p>
                            <div className="tooltipIconStyle">
                              <OverlayTrigger
                                delay={{ hide: 10, show: 10 }}
                                overlay={(props) => (
                                  <Tooltip
                                    placement="bottom"
                                    positionLeft
                                    {...props}
                                  >
                                    {t("TICKET_PASSES_PER_UNIT_HINT")}
                                  </Tooltip>
                                )}
                                placement="bottom"
                              >
                                <i className="mdi mdi-alert-circle-outline" />
                              </OverlayTrigger>
                            </div>
                          </div>
                        </div>
                        <div className="input-column-style ticket-cover-column">
                          <div className="d-flex">
                            <p className="form-label">
                              {t("TICKET_COVER_AMOUNT")}
                            </p>
                            <div className="tooltipIconStyle">
                              <OverlayTrigger
                                delay={{ hide: 10, show: 10 }}
                                overlay={(props) => (
                                  <Tooltip
                                    placement="bottom"
                                    positionLeft
                                    {...props}
                                  >
                                    {t("TICKET_COVER_AMOUNT_HINT")}
                                  </Tooltip>
                                )}
                                placement="bottom"
                              >
                                <i className="mdi mdi-alert-circle-outline" />
                              </OverlayTrigger>
                            </div>
                          </div>
                        </div>
                        {/* Home delivery column — hidden for now
                        <div className="input-column-style">
                          <div className="d-flex">
                            <p className="form-label">
                              {placeHolderShimer && <CustomeShimer />}
                              {t("HOME_DELIVERY_AMOUNT")}    <OverlayTrigger
                                delay={{ hide: 10, show: 10 }}
                                overlay={(props) => (
                                  <Tooltip
                                    className={'homedeliveryStyle'}
                                    placement="bottom"
                                    positionLeft
                                    {...props}
                                  >
                                    Leave the value blank for no home delivery<br />
                                    Enter "0" for free home delivery
                                  </Tooltip>
                                )}
                                placement="bottom"
                              >
                                <i className="mdi mdi-alert-circle-outline" style={{ marginLeft: 0, top: 0, position: 'relative', color: 'white' }} />
                              </OverlayTrigger>
                            </p>
                          </div>
                        </div>
                        */}
                        <div className="input-column-style ticket-category-column">
                          <div className="d-flex">
                            <p className="form-label">
                              {placeHolderShimer && <CustomeShimer />}
                              {t("CATEGORY_DESCRIPTION")}
                            </p>
                          </div>
                        </div>
                        <div className="soldOutToggle soldOutToggle--online">
                          <div className="d-flex">
                            <p className="form-label">
                              {t("TICKET_ONLINE_QUEUE_SOLD_OUT")}
                            </p>
                            <p className="requiredStyle">*</p>
                            <div className="tooltipIconStyle">
                              <OverlayTrigger
                                delay={{ hide: 10, show: 10 }}
                                overlay={(props) => (
                                  <Tooltip
                                    placement="bottom"
                                    positionLeft
                                    {...props}
                                  >
                                    {t("TICKET_ONLINE_QUEUE_SOLD_OUT_HINT")}
                                  </Tooltip>
                                )}
                                placement="bottom"
                              >
                                <i className="mdi mdi-alert-circle-outline" />
                              </OverlayTrigger>
                            </div>
                          </div>
                        </div>
                        <div className="soldOutToggle soldOutToggle--offline">
                          <div className="d-flex">
                            <p className="form-label">
                              {t("TICKET_OFFLINE_SOLD_OUT")}
                            </p>
                            <div className="tooltipIconStyle">
                              <OverlayTrigger
                                delay={{ hide: 10, show: 10 }}
                                overlay={(props) => (
                                  <Tooltip
                                    placement="bottom"
                                    positionLeft
                                    {...props}
                                  >
                                    {t("TICKET_OFFLINE_SOLD_OUT_HINT")}
                                  </Tooltip>
                                )}
                                placement="bottom"
                              >
                                <i className="mdi mdi-alert-circle-outline" />
                              </OverlayTrigger>
                            </div>
                          </div>
                        </div>
                      </div>

                      {fields.map(
                        (
                          {
                            tickettype,
                            categorydesc,
                            _id,
                            ticketprice,
                            gstPercent,
                            totaltickets,
                            // deliverycost,
                            passesPerUnit,
                            coverAmount,
                            soldout,
                            offlineSoldOut,
                            id,
                          },
                          index
                        ) => {
                          return (
                            <div
                              key={id}
                              id={_id}
                              className="addTicketRowStyle"
                            >
                              <div className="input-column-style ticket-type-column">
                                <div className="wrapper-input btmDrpdown numberFeildWithoutSymbol">
                                  {placeHolderShimer && <CustomeShimer />}
                                  <TicketTypeSelect
                                    control={control}
                                    rules={eventForm?.tickettype?.validate}
                                    name={`tickets.${index}.tickettype`}
                                    errors={errors}
                                    forceShowError={fieldHasError(
                                      `tickets.${index}.tickettype`
                                    )}
                                    placeholder={t("SELECT")}
                                    formatCreateLabel={(input) =>
                                      t("TICKET_TYPE_CREATE", {
                                        type: input.trim(),
                                      })
                                    }
                                    onValueChange={(nextType) => {
                                      const autoPasses =
                                        getAutoPassesPerUnit(nextType);
                                      if (autoPasses != null) {
                                        setValue(
                                          `tickets.${index}.passesPerUnit`,
                                          String(autoPasses),
                                          { shouldDirty: true, shouldValidate: true }
                                        );
                                      }
                                    }}
                                  />
                                  {errors.tickets &&
                                    errors.tickets[index]?.tickettype && (
                                      <div className="errorMsg">
                                        <span>
                                          {
                                            errors.tickets[index].tickettype
                                              .message
                                          }
                                        </span>
                                      </div>
                                    )}
                                </div>
                              </div>
                              <div className="input-column-style ticket-gst-column">
                                <div className="wrapper-input btmDrpdown numberFeildWithoutSymbol">
                                  {placeHolderShimer && <CustomeShimer />}
                                  <Controller
                                    control={control}
                                    name={`tickets.${index}.gstPercent`}
                                    rules={eventForm?.gstPercent?.validate}
                                    defaultValue={
                                      ["0", "5", "18"].includes(
                                        String(gstPercent)
                                      )
                                        ? String(gstPercent)
                                        : "0"
                                    }
                                    render={({
                                      field: { onChange, value, ref },
                                    }) => {
                                      const allowed = ["0", "5", "18"];
                                      const numeric = String(Number(value));
                                      const selected = allowed.includes(numeric)
                                        ? numeric
                                        : "0";
                                      return (
                                        <select
                                          ref={ref}
                                          className={`form-control ticket-gst-select${
                                            fieldHasError(
                                              `tickets.${index}.gstPercent`
                                            )
                                              ? " is-invalid"
                                              : ""
                                          }`}
                                          value={selected}
                                          onChange={(e) =>
                                            onChange(e.target.value)
                                          }
                                        >
                                          <option value="0">0</option>
                                          <option value="5">5</option>
                                          <option value="18">18</option>
                                        </select>
                                      );
                                    }}
                                  />
                                  {errors.tickets &&
                                    errors.tickets[index]?.gstPercent && (
                                      <div className="errorMsg">
                                        <span>
                                          {
                                            errors.tickets[index].gstPercent
                                              .message
                                          }
                                        </span>
                                      </div>
                                    )}
                                </div>
                              </div>
                              <div className="input-column-style ticket-price-column">
                                <div className="wrapper-input btmDrpdown numberFeild">
                                  {placeHolderShimer && <CustomeShimer />}
                                  <div className="iconBefore">
                                    <TextField1
                                      control={control}
                                      watchValue={watch(
                                        `tickets.${index}.ticketprice`
                                      )}
                                      rules={eventForm?.ticketprice?.validate}
                                      name={`tickets.${index}.ticketprice`}
                                      errors={errors}
                                      forceShowError={fieldHasError(
                                        `tickets.${index}.ticketprice`
                                      )}
                                      autoFocus={false}
                                      required={false}
                                      placeholder={t("AMOUNT_PLACEHOLDER")}
                                      type="text"
                                      onKeyDown={handleKeyDown}
                                      onChange={() => {
                                        if (
                                          watch(
                                            `tickets.${index}.coverAmount`
                                          )
                                        ) {
                                          trigger(
                                            `tickets.${index}.coverAmount`
                                          );
                                        }
                                      }}
                                      value={ticketprice}
                                      defaultValue={ticketprice}
                                      maxLength={8}
                                    />
                                  </div>
                                </div>
                              </div>
                              <div className="input-column-style ticket-total-column">
                                <div className="wrapper-input btmDrpdown numberFeildWithoutSymbol">
                                  {placeHolderShimer && <CustomeShimer />}
                                  <TextField1
                                    control={control}
                                    watchValue={watch(
                                      `tickets.${index}.totaltickets`
                                    )}
                                    rules={eventForm?.totaltickets?.validate}
                                    name={`tickets.${index}.totaltickets`}
                                    errors={errors}
                                    forceShowError={fieldHasError(
                                      `tickets.${index}.totaltickets`
                                    )}
                                    autoFocus={false}
                                    required={false}
                                    placeholder={t(
                                      "ENTER_TOTAL_NUMBER_OF_TICKETS"
                                    )}
                                    type="text"
                                    onKeyDown={handleKeyDown}
                                    value={totaltickets} // bind the value
                                    defaultValue={totaltickets}
                                    maxLength={8}
                                  />
                                </div>
                              </div>
                              <div className="input-column-style ticket-passes-column">
                                <div className="wrapper-input btmDrpdown numberFeildWithoutSymbol">
                                  {placeHolderShimer && <CustomeShimer />}
                                  <TextField1
                                    control={control}
                                    watchValue={watch(
                                      `tickets.${index}.passesPerUnit`
                                    )}
                                    rules={eventForm?.passesPerUnit?.validate}
                                    name={`tickets.${index}.passesPerUnit`}
                                    errors={errors}
                                    forceShowError={fieldHasError(
                                      `tickets.${index}.passesPerUnit`
                                    )}
                                    autoFocus={false}
                                    required={false}
                                    placeholder={t(
                                      "TICKET_PASSES_PER_UNIT_PLACEHOLDER"
                                    )}
                                    type="text"
                                    onKeyDown={handleKeyDown}
                                    value={passesPerUnit}
                                    defaultValue={passesPerUnit}
                                    maxLength={2}
                                  />
                                </div>
                              </div>
                              <div className="input-column-style ticket-cover-column">
                                <div className="wrapper-input btmDrpdown numberFeildWithoutSymbol">
                                  {placeHolderShimer && <CustomeShimer />}
                                  <TextField1
                                    control={control}
                                    watchValue={watch(
                                      `tickets.${index}.coverAmount`
                                    )}
                                    rules={{
                                      ...eventForm?.coverAmount?.validate,
                                      validate: (value) => {
                                        const baseValidate =
                                          eventForm?.coverAmount?.validate
                                            ?.validate;
                                        if (baseValidate) {
                                          const baseResult =
                                            baseValidate(value);
                                          if (baseResult !== true)
                                            return baseResult;
                                        }
                                        if (value === "" || value == null)
                                          return true;
                                        const cover = Number(
                                          String(value).trim()
                                        );
                                        const priceRaw = watch(
                                          `tickets.${index}.ticketprice`
                                        );
                                        const price = Number(
                                          String(priceRaw ?? "").trim()
                                        );
                                        if (
                                          priceRaw !== "" &&
                                          priceRaw != null &&
                                          !Number.isNaN(price) &&
                                          !Number.isNaN(cover) &&
                                          cover > price
                                        ) {
                                          return t(
                                            "TICKET_COVER_AMOUNT_EXCEEDS_PRICE"
                                          );
                                        }
                                        return true;
                                      },
                                    }}
                                    name={`tickets.${index}.coverAmount`}
                                    errors={errors}
                                    forceShowError={fieldHasError(
                                      `tickets.${index}.coverAmount`
                                    )}
                                    autoFocus={false}
                                    required={false}
                                    placeholder={t(
                                      "TICKET_COVER_AMOUNT_PLACEHOLDER"
                                    )}
                                    type="text"
                                    onKeyDown={handleKeyDown}
                                    value={coverAmount}
                                    defaultValue={coverAmount}
                                    maxLength={8}
                                  />
                                </div>
                              </div>
                              {/* Home delivery field — hidden for now
                              <div className="input-column-style">
                                <OverlayTrigger
                                  delay={{ hide: 10, show: 10 }}
                                  overlay={(props) => (
                                    <Tooltip
                                      className={'homedeliveryStyle'}
                                      placement="bottom"
                                      positionLeft
                                      {...props}
                                    >
                                      Leave the value blank for no home delivery<br />
                                      Enter "0" for free home delivery
                                    </Tooltip>
                                  )}
                                  placement="bottom"
                                >
                                  <i className="mdi mdi-alert-circle-outline desktopTooltipView" style={{ color: 'white' }} />
                                </OverlayTrigger>
                                <div className="wrapper-input btmDrpdown numberFeild">
                                  {placeHolderShimer && <CustomeShimer />}

                                  <div className="iconBefore">
                                    <TextField1
                                      control={control}
                                      watchValue={watch(
                                        `tickets.${index}.deliverycost`
                                      )}
                                      rules={eventForm?.deliverycost?.validate}
                                      name={`tickets.${index}.deliverycost`}
                                      errors={errors}
                                      forceShowError={fieldHasError(
                                        `tickets.${index}.deliverycost`
                                      )}
                                      autoFocus={false}
                                      required={false}
                                      type="text"
                                      onKeyDown={handleKeyDown}
                                      value={deliverycost}
                                      defaultValue={deliverycost}
                                      maxLength={5}
                                    />
                                  </div>
                                </div>
                              </div>
                              */}
                              <div className="input-column-style ticket-category-column">
                                <div className="wrapper-input btmDrpdown numberFeildWithoutSymbol">
                                  {placeHolderShimer && <CustomeShimer />}
                                  <TextField1
                                    control={control}
                                    watchValue={watch(
                                      `tickets.${index}.categorydesc`
                                    )}
                                    rules={eventForm?.categorydesc?.validate}
                                    name={`tickets.${index}.categorydesc`}
                                    errors={errors}
                                    forceShowError={fieldHasError(
                                      `tickets.${index}.categorydesc`
                                    )}
                                    autoFocus={false}
                                    required={false}
                                    placeholder={t(
                                      "ENTER_CATEGORY_DESCRIPTION"
                                    )}
                                    type="text"
                                    onKeyDown={handleCategoryDescKeyDown}
                                    onChange={handleCategoryDescChange(
                                      `tickets.${index}.categorydesc`
                                    )}
                                    value={categorydesc}
                                    defaultValue={categorydesc}
                                    maxLength={200}
                                  />
                                </div>
                              </div>
                              <div className="soldOutToggle soldOutToggle--online">
                                <OverlayTrigger
                                  delay={{ hide: 10, show: 10 }}
                                  overlay={(props) => (
                                    <Tooltip
                                      placement="bottom"
                                      positionLeft
                                      {...props}
                                    >
                                      Once Marked as sold out, it will not allow
                                      new users to join the queue.
                                    </Tooltip>
                                  )}
                                  placement="bottom"
                                >
                                  <i style={{ color: 'white' }} className="mdi mdi-alert-circle-outline tooltipSoldOutMobile" />
                                </OverlayTrigger>
                                <div className="soldOutToggleRowStyle">
                                  <Controller
                                    name={`tickets.${index}.soldout`}
                                    rules={eventForm?.soldout?.validate}
                                    control={control}
                                    defaultValue={soldout}
                                    render={({
                                      field: { onChange, value },
                                    }) => (
                                      <>
                                        <CustomToggleButton
                                          name={`tickets.${index}.soldout`}
                                          rules={eventForm?.soldout?.validate}
                                          control={control}
                                          id=""
                                          checked={value}
                                          onCheckChange={onChange}
                                        />
                                      </>
                                    )}
                                  />
                                  {errors.tickets &&
                                    errors.tickets[index]?.soldout && (
                                      <div className="errorMsg">
                                        <span>
                                          {
                                            errors.tickets[index].soldout
                                              .message
                                          }
                                        </span>
                                      </div>
                                    )}
                                </div>
                              </div>
                              <div className="soldOutToggle soldOutToggle--offline">
                                <OverlayTrigger
                                  delay={{ hide: 10, show: 10 }}
                                  overlay={(props) => (
                                    <Tooltip
                                      placement="bottom"
                                      positionLeft
                                      {...props}
                                    >
                                      {t("TICKET_OFFLINE_SOLD_OUT_HINT")}
                                    </Tooltip>
                                  )}
                                  placement="bottom"
                                >
                                  <i
                                    style={{ color: "white" }}
                                    className="mdi mdi-alert-circle-outline tooltipSoldOutMobile"
                                  />
                                </OverlayTrigger>
                                <div className="soldOutToggleRowStyle">
                                  <Controller
                                    name={`tickets.${index}.offlineSoldOut`}
                                    rules={eventForm?.soldout?.validate}
                                    control={control}
                                    defaultValue={offlineSoldOut}
                                    render={({
                                      field: { onChange, value },
                                    }) => (
                                      <CustomToggleButton
                                        name={`tickets.${index}.offlineSoldOut`}
                                        rules={eventForm?.soldout?.validate}
                                        control={control}
                                        id=""
                                        checked={Boolean(value)}
                                        onCheckChange={onChange}
                                      />
                                    )}
                                  />
                                </div>
                              </div>
                              {id ? (
                                <button
                                  type="button"
                                  onClick={() => remove(index)}
                                  className="removeBtnStyle"
                                >
                                  <i className="bx bx-minus-circle"></i>
                                </button>
                              ) : null}
                              {/* Enable phased pricing — hidden for now
                              <TicketPhaseBuilder
                                compact
                                ticket={
                                  watch(`tickets.${index}`) || {
                                    tickettype,
                                    ticketprice,
                                    totaltickets,
                                    soldout,
                                    phasedPricingEnabled: false,
                                    phases: [],
                                  }
                                }
                                onChange={(next) => {
                                  const synced = applyPhaseTotalsToTicket(next);
                                  setValue(
                                    `tickets.${index}.phasedPricingEnabled`,
                                    Boolean(synced.phasedPricingEnabled),
                                    { shouldDirty: true }
                                  );
                                  setValue(
                                    `tickets.${index}.phases`,
                                    synced.phases || [],
                                    { shouldDirty: true }
                                  );
                                  if (synced.phasedPricingEnabled) {
                                    setValue(
                                      `tickets.${index}.totaltickets`,
                                      String(synced.totaltickets ?? ""),
                                      { shouldDirty: true, shouldValidate: true }
                                    );
                                    setValue(
                                      `tickets.${index}.ticketprice`,
                                      String(synced.ticketprice ?? ""),
                                      { shouldDirty: true, shouldValidate: true }
                                    );
                                  }
                                }}
                              />
                              */}
                            </div>
                          );
                        }
                      )}
                      </div>
                    </div>

                    {showTableBookingField ? (
                      <div className="event-table-booking-below-tickets">
                        <div className="titleBtnRowStlye event-table-booking-header-row">
                          <div className="addTicketTitleStyle">
                            {t("EVENT_TABLE_BOOKING_TITLE")}
                          </div>
                          <div className="event-table-booking-header-toggle">
                            <Controller
                              name="tableBookingEnabled"
                              control={control}
                              render={({ field: { onChange, value } }) => (
                                <CustomToggleButton
                                  name="tableBookingEnabled"
                                  checked={Boolean(value)}
                                  onCheckChange={(checked) => {
                                    onChange(checked);
                                    if (!checked) {
                                      setValue("tableBookingAreaPricing", [], {
                                        shouldDirty: true,
                                      });
                                    }
                                  }}
                                />
                              )}
                            />
                          </div>
                        </div>
                        <EventTableBookingSection
                          control={control}
                          setValue={setValue}
                          effectiveClubId={effectiveClubIdForTableBooking}
                          eventId={state?.item?._id}
                          showToggle={false}
                        />
                        {state?.item?._id ? (
                          <p className="eph-tier-meta mt-2 mb-0">
                            <Link
                              to={`/events-list/event-phases?eventId=${state.item._id}`}
                            >
                              {t("EVENT_HIKE_OPEN_HUB")}
                            </Link>
                          </p>
                        ) : null}
                      </div>
                    ) : null}

                    {effectiveClubIdForCatalog && canManageCatalog ? (
                      <EventCatalogAttachSection
                        control={control}
                        setValue={setValue}
                        clubId={effectiveClubIdForCatalog}
                        eventItem={catalogEventItem || state?.item}
                        catalogManageHref={catalogManageHref}
                        canManageCatalog={canManageCatalog}
                      />
                    ) : null}

                    <div className="mt-3 d-flex FormButton">
                      <div className="wrapper-input">
                        {placeHolderShimer && <CustomeShimer />}

                        <CustomButton
                          className="black-btn Fill_Button eventPrimaryBtn"
                          type="submit"
                          disabled={
                            state?.page === "add"
                              ? false
                              : !formState.isValid || !formState.isDirty
                          }
                          loading={createEventLoaderButton}
                        >
                          {t("EVENT_PREVIEW_BUTTON")}
                        </CustomButton>
                        {isCreatePage ? (
                          <CustomButton
                            className="cancel-btn eventDraftBtn"
                            type="button"
                            onClick={saveCreateEventDraftNow}
                          >
                            SAVE DRAFT
                          </CustomButton>
                        ) : null}

                        <CustomButton
                          className="cancel-btn eventCancelBtn"
                          type="button"
                          onClick={() => {
                            navigate("/events-list");
                          }}
                        >
                          {t("CANCEL")}
                        </CustomButton>
                      </div>
                    </div>
                  </div>
                </form>
                {eventPreview ? (
                  <EventFormPreview
                    preview={eventPreview}
                    isCreate={state?.page === "add"}
                    loading={createEventLoaderButton}
                    onBack={handleCloseEventPreview}
                    onConfirm={() => handleFormSubmit(eventPreview.data)}
                    showClubField={showClubField}
                    showTableBookingField={showTableBookingField}
                    showStaffField={showOfflineEmployeeField}
                    clubEmployeeOptions={clubEmployeeOptions}
                    catalogClubId={
                      effectiveClubIdForCatalog && canManageCatalog
                        ? effectiveClubIdForCatalog
                        : ""
                    }
                    tableClubId={effectiveClubIdForTableBooking}
                    profileImage={profileImage}
                    daynamicImage={daynamicImage}
                    islayoutImage={islayoutImage}
                    daynamicLayoutImage={daynamicLayoutImage}
                    locationValue={locationValue}
                  />
                ) : null}
              </div>
            </Card.Body>
          </Card>
          <ImageCropModal
            show={!!cropModalSession}
            imageSrc={cropModalSession?.src}
            aspect={cropModalSession?.aspect ?? EVENT_IMAGE_ASPECT}
            outputWidth={cropModalSession?.outputWidth}
            outputHeight={cropModalSession?.outputHeight}
            hintKey={cropModalSession?.hintKey}
            mimeType={cropModalSession?.mime}
            fileBaseName={cropModalSession?.baseName}
            onHide={() => setCropModalSession(null)}
            onCropped={handleCroppedImageApplied}
          />
        </div>
      </React.Fragment>
    </>
  );
};

export default CreateEditEventComponent1;
